class IndividualHandlingFeeController {
  constructor(config) {
    this.enabled = Boolean(config?.enabled);
    this.variantId = Number(config?.variantId || 0);
    this.syncPromise = null;
  }

  get isConfigured() {
    return this.enabled && Number.isInteger(this.variantId) && this.variantId > 0;
  }

  isIndividualLine(item) {
    return String(item?.properties?._individual_product) === 'true';
  }

  isFeeLine(item) {
    return (
      Number(item?.variant_id) === this.variantId ||
      String(item?.properties?._individual_handling_fee) === 'true'
    );
  }

  async getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to read the cart for the individual-product handling fee.');
    return response.json();
  }

  sectionRequest(cartComponent) {
    if (!cartComponent?.getSectionsToRender) return {};

    return {
      sections: cartComponent.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }

  async addFee(cartComponent) {
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: this.variantId,
            quantity: 1,
            properties: { _individual_handling_fee: 'true' },
          },
        ],
        ...this.sectionRequest(cartComponent),
      }),
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || 'Unable to add the individual-product handling fee.');
    }
    return result;
  }

  async changeFee(feeLine, quantity, cartComponent) {
    const response = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: feeLine.key,
        quantity,
        ...this.sectionRequest(cartComponent),
      }),
    });

    const result = await response.json();
    if (!response.ok || result.errors) {
      throw new Error(result.description || result.errors || 'Unable to update the individual-product handling fee.');
    }
    return result;
  }

  async ensureFeeForProductAdd(productResponse, cartComponent) {
    if (!this.isConfigured) return productResponse;

    try {
      const cart = await this.getCart();
      const feeLine = cart.items.find((item) => this.isFeeLine(item));

      if (!feeLine) return await this.addFee(cartComponent);
      if (feeLine.quantity !== 1) return await this.changeFee(feeLine, 1, cartComponent);
    } catch (error) {
      console.error(error);
    }

    return productResponse;
  }

  async sync() {
    if (!this.isConfigured) return false;
    if (this.syncPromise) return this.syncPromise;

    this.syncPromise = (async () => {
      const cart = await this.getCart();
      const hasIndividualProduct = cart.items.some((item) => this.isIndividualLine(item));
      const feeLine = cart.items.find((item) => this.isFeeLine(item));
      let result = null;

      if (hasIndividualProduct && !feeLine) {
        result = await this.addFee();
      } else if (hasIndividualProduct && feeLine.quantity !== 1) {
        result = await this.changeFee(feeLine, 1);
      } else if (!hasIndividualProduct && feeLine) {
        result = await this.changeFee(feeLine, 0);
      }

      if (result && typeof publish === 'function') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'individual-handling-fee',
          cartData: result,
        });
      }

      return Boolean(result);
    })()
      .catch((error) => {
        console.error(error);
        return false;
      })
      .finally(() => {
        this.syncPromise = null;
      });

    return this.syncPromise;
  }

  start() {
    if (!this.isConfigured) return;

    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'individual-handling-fee') return;
        return this.sync();
      });
    }

    this.sync();
  }
}

window.individualHandlingFeeController = new IndividualHandlingFeeController(window.individualHandlingFee);
window.individualHandlingFeeController.start();
